import { IDBPDatabase, openDB } from "idb";

import { StoreConfig } from "../types";
import {
    DB_NAME,
    DB_VERSION,
    INDEX_KEY_PATHS,
    INDEX_NAMES,
    KEY_PATHS,
    LEGACY_CHAT_STORES,
    STORES,
} from "./constants";

// Store configurations
export const STORE_CONFIGS: Record<string, StoreConfig> = {
    [STORES.USER_INFO]: {
        name: STORES.USER_INFO,
        keyPath: KEY_PATHS.USER_INFO,
        indexes: [
            { name: INDEX_NAMES.USER_INFO, keyPath: INDEX_KEY_PATHS.USER_INFO, unique: false },
        ],
    },
    [STORES.INBOX]: {
        name: STORES.INBOX,
        keyPath: KEY_PATHS.INBOX,
    },
    [STORES.ACTIVITY_MESSAGES]: {
        name: STORES.ACTIVITY_MESSAGES,
        keyPath: KEY_PATHS.ACTIVITY_MESSAGES,
    },
    [STORES.TASK_META]: {
        name: STORES.TASK_META,
        keyPath: KEY_PATHS.TASK_META,
        indexes: [
            { name: INDEX_NAMES.TASK_META, keyPath: INDEX_KEY_PATHS.TASK_META, unique: false },
            {
                name: INDEX_NAMES.TASK_META_COMPOUND,
                keyPath: [
                    INDEX_KEY_PATHS.TASK_META_COMPOUND[0],
                    INDEX_KEY_PATHS.TASK_META_COMPOUND[1],
                ],
                unique: false,
            },
            {
                name: INDEX_NAMES.TASK_META_ASSIGNEE,
                keyPath: INDEX_KEY_PATHS.TASK_META_ASSIGNEE,
                unique: false,
            },
            {
                name: INDEX_NAMES.TASK_META_ASSIGNEE_PROJECT,
                keyPath: [
                    INDEX_KEY_PATHS.TASK_META_ASSIGNEE_PROJECT[0],
                    INDEX_KEY_PATHS.TASK_META_ASSIGNEE_PROJECT[1],
                ],
                unique: false,
            },
        ],
    },
    [STORES.TASK_FULL]: {
        name: STORES.TASK_FULL,
        keyPath: KEY_PATHS.TASK_FULL,
        indexes: [
            {
                name: INDEX_NAMES.TASK_FULL_LRU,
                keyPath: INDEX_KEY_PATHS.TASK_FULL_LRU,
                unique: false,
            },
        ],
    },
    [STORES.PERSONAL_NOTES]: {
        name: STORES.PERSONAL_NOTES,
        keyPath: KEY_PATHS.PERSONAL_NOTES,
        indexes: [
            {
                name: INDEX_NAMES.PERSONAL_NOTES_OWNER,
                keyPath: INDEX_KEY_PATHS.PERSONAL_NOTES_OWNER,
                unique: false,
            },
        ],
    },
    [STORES.TASK_NOTES]: {
        name: STORES.TASK_NOTES,
        keyPath: KEY_PATHS.TASK_NOTES,
        indexes: [
            {
                name: INDEX_NAMES.TASK_NOTES_OWNER,
                keyPath: INDEX_KEY_PATHS.TASK_NOTES_OWNER,
                unique: false,
            },
            {
                name: INDEX_NAMES.TASK_NOTES_TASK,
                keyPath: INDEX_KEY_PATHS.TASK_NOTES_TASK,
                unique: false,
            },
        ],
    },
    [STORES.CHAT_NOTES]: {
        name: STORES.CHAT_NOTES,
        keyPath: KEY_PATHS.CHAT_NOTES,
        indexes: [
            {
                name: INDEX_NAMES.CHAT_NOTES_OWNER,
                keyPath: INDEX_KEY_PATHS.CHAT_NOTES_OWNER,
                unique: false,
            },
            {
                name: INDEX_NAMES.CHAT_NOTES_CHAT,
                keyPath: INDEX_KEY_PATHS.CHAT_NOTES_CHAT,
                unique: false,
            },
        ],
    },
    [STORES.TODOS]: {
        name: STORES.TODOS,
        keyPath: KEY_PATHS.TODOS,
        indexes: [{ name: INDEX_NAMES.TODOS, keyPath: INDEX_KEY_PATHS.TODOS, unique: false }],
    },
    // Per-data-type "last synced" watermarks for incremental sync.
    // Record shape: { key, serverTime, lastUpdated }. The `key` is a
    // stable string per loader (e.g. "activity", "dm", "tasks:<projectId>");
    // `serverTime` is the ISO timestamp returned by the backend's delta
    // endpoint, which the next sync replays as `?since=`.
    [STORES.SYNC_CHECKPOINTS]: {
        name: STORES.SYNC_CHECKPOINTS,
        keyPath: KEY_PATHS.SYNC_CHECKPOINTS,
    },
    // ---- v9: unified messaging stores ----
    // All keyed by server UUID `id`. Channel-scoped reads use a
    // by-channel index; the per-channel ordering uses the compound
    // `(channelId, seq)` index. PM grouping is a render-time selector
    // backed by `(channelId, metadata.taskId via the writer-projected
    // taskKey)` — see the writer comment in `channelService.ts`.
    [STORES.CHANNELS]: {
        name: STORES.CHANNELS,
        keyPath: KEY_PATHS.CHANNELS,
        indexes: [
            {
                name: INDEX_NAMES.CHANNELS_BY_KIND,
                keyPath: INDEX_KEY_PATHS.CHANNELS_BY_KIND,
                unique: false,
            },
        ],
    },
    [STORES.CHANNEL_MEMBERS]: {
        name: STORES.CHANNEL_MEMBERS,
        keyPath: KEY_PATHS.CHANNEL_MEMBERS,
        indexes: [
            {
                name: INDEX_NAMES.CHANNEL_MEMBERS_BY_CHANNEL,
                keyPath: INDEX_KEY_PATHS.CHANNEL_MEMBERS_BY_CHANNEL,
                unique: false,
            },
            {
                name: INDEX_NAMES.CHANNEL_MEMBERS_BY_USER,
                keyPath: INDEX_KEY_PATHS.CHANNEL_MEMBERS_BY_USER,
                unique: false,
            },
        ],
    },
    [STORES.MESSAGES_V3]: {
        name: STORES.MESSAGES_V3,
        keyPath: KEY_PATHS.MESSAGES_V3,
        indexes: [
            {
                name: INDEX_NAMES.MESSAGES_V3_BY_CHANNEL,
                keyPath: INDEX_KEY_PATHS.MESSAGES_V3_BY_CHANNEL,
                unique: false,
            },
            {
                name: INDEX_NAMES.MESSAGES_V3_BY_CHANNEL_SEQ,
                keyPath: [
                    INDEX_KEY_PATHS.MESSAGES_V3_BY_CHANNEL_SEQ[0],
                    INDEX_KEY_PATHS.MESSAGES_V3_BY_CHANNEL_SEQ[1],
                ],
                // unique: catches double-write under race; cf. the
                // backend's `(channel, seq)` UNIQUE constraint.
                unique: true,
            },
            {
                name: INDEX_NAMES.MESSAGES_V3_BY_THREAD_ROOT,
                keyPath: INDEX_KEY_PATHS.MESSAGES_V3_BY_THREAD_ROOT,
                unique: false,
            },
            {
                name: INDEX_NAMES.MESSAGES_V3_BY_CHANNEL_TASK,
                keyPath: [
                    INDEX_KEY_PATHS.MESSAGES_V3_BY_CHANNEL_TASK[0],
                    INDEX_KEY_PATHS.MESSAGES_V3_BY_CHANNEL_TASK[1],
                ],
                unique: false,
            },
        ],
    },
    [STORES.MESSAGE_REACTIONS]: {
        name: STORES.MESSAGE_REACTIONS,
        keyPath: KEY_PATHS.MESSAGE_REACTIONS,
        indexes: [
            {
                name: INDEX_NAMES.MESSAGE_REACTIONS_BY_MESSAGE,
                keyPath: INDEX_KEY_PATHS.MESSAGE_REACTIONS_BY_MESSAGE,
                unique: false,
            },
        ],
    },
    [STORES.READ_CURSORS]: {
        name: STORES.READ_CURSORS,
        keyPath: KEY_PATHS.READ_CURSORS,
        indexes: [
            {
                name: INDEX_NAMES.READ_CURSORS_BY_CHANNEL,
                keyPath: INDEX_KEY_PATHS.READ_CURSORS_BY_CHANNEL,
                unique: false,
            },
        ],
    },
    [STORES.PINS]: {
        name: STORES.PINS,
        keyPath: KEY_PATHS.PINS,
        indexes: [
            {
                name: INDEX_NAMES.PINS_BY_USER,
                keyPath: INDEX_KEY_PATHS.PINS_BY_USER,
                unique: false,
            },
        ],
    },
    [STORES.FLAGS]: {
        name: STORES.FLAGS,
        keyPath: KEY_PATHS.FLAGS,
        indexes: [
            {
                name: INDEX_NAMES.FLAGS_BY_USER,
                keyPath: INDEX_KEY_PATHS.FLAGS_BY_USER,
                unique: false,
            },
        ],
    },
    [STORES.MESSAGE_ATTACHMENTS]: {
        name: STORES.MESSAGE_ATTACHMENTS,
        keyPath: KEY_PATHS.MESSAGE_ATTACHMENTS,
        indexes: [
            {
                name: INDEX_NAMES.MESSAGE_ATTACHMENTS_BY_MESSAGE,
                keyPath: INDEX_KEY_PATHS.MESSAGE_ATTACHMENTS_BY_MESSAGE,
                unique: false,
            },
        ],
    },
};

// Initialize database with proper schema.
//
// Upgrade rules:
//   - If a store is missing, create it and all of its declared indexes.
//   - If a store already exists, only add indexes that are not yet present.
//     Existing indexes are never recreated or renamed here. To rename or
//     repurpose an index, drop and re-add it in a dedicated migration step.
export const initDB = async (): Promise<IDBPDatabase> => {
    // `db` is referenced inside the `blocking` handler below; that
    // callback only fires long after `openDB` resolves, so the binding is
    // assigned by then (standard idb close-on-blocking pattern).
    const db = await openDB(DB_NAME, DB_VERSION, {
        upgrade(db, oldVersion, _newVersion, tx) {
            // v8: the TODOS store's record shape changed (ToDoFact →
            // ToDoGroup), so the keyPath moved from "todoId" to
            // "groupId". Old rows are unreadable under the new shape;
            // drop the store so the generic loop below recreates it.
            if (oldVersion < 8 && db.objectStoreNames.contains("todos")) {
                db.deleteObjectStore("todos");
            }
            // v10: drop the legacy per-type chat stores. The v3
            // unified messaging stores have been the source of truth
            // for chat list, messages, threads, flags, pins, and read
            // cursors for several batches; the legacy stores have had
            // no writers or readers in that time. Delete by exact
            // store name so users on an older DB version don't keep
            // empty `dmChats` / `dmMessages` / … lying around.
            if (oldVersion < 10) {
                for (const name of LEGACY_CHAT_STORES) {
                    if (db.objectStoreNames.contains(name)) {
                        db.deleteObjectStore(name);
                    }
                }
            }
            Object.values(STORE_CONFIGS).forEach((config) => {
                if (!db.objectStoreNames.contains(config.name)) {
                    const store = db.createObjectStore(config.name, {
                        keyPath: config.keyPath,
                    });
                    config.indexes?.forEach((index) => {
                        store.createIndex(index.name, index.keyPath, {
                            unique: index.unique || false,
                        });
                    });
                    return;
                }

                if (!config.indexes?.length) return;

                const store = tx.objectStore(config.name);
                const existing = new Set<string>(Array.from(store.indexNames));
                config.indexes.forEach((index) => {
                    if (existing.has(index.name)) return;
                    store.createIndex(index.name, index.keyPath, {
                        unique: index.unique || false,
                    });
                });
            });
        },
        // Another tab holds an OLDER connection open, blocking THIS tab's
        // upgrade. We can't force the other tab shut from here, but its own
        // `blocking` handler closes it when it sees our newer open. Log so a
        // stuck upgrade is diagnosable instead of a silent forever-hang
        // (which is what happened before: no handler → every IDB read/write
        // in this tab stalled with no error after a version bump like v10).
        blocked(currentVersion, blockedVersion) {
            console.warn(
                `[IDB] open blocked: another tab holds v${currentVersion}; ` +
                    `waiting to upgrade to v${blockedVersion}.`
            );
        },
        // THIS connection is the older one blocking a NEWER-version open in
        // another tab. Close it so that tab's upgrade can proceed — otherwise
        // both tabs deadlock (the new one waits forever on `blocked`). The
        // in-memory store stays authoritative; callers already treat a
        // closed DB as a transient/degraded cache error, and a reload
        // re-opens at the new version.
        blocking(currentVersion, blockedVersion) {
            console.warn(
                `[IDB] closing v${currentVersion} connection so another tab ` +
                    `can upgrade to v${blockedVersion}.`
            );
            db.close();
        },
        terminated() {
            console.warn("[IDB] connection terminated unexpectedly by the browser.");
        },
    });
    return db;
};
